import { MANAGE } from '@/constants/testIds';
import { Award, Lock } from 'lucide-react';

const TIER_STYLES = {
  bronze: { color: '#CD7F32', label: 'BRONZE' },
  silver: { color: '#C0C0C0', label: 'SILVER' },
  gold:   { color: '#FFD700', label: 'GOLD' },
};

export const MilestoneCard = ({ refData, currentTier }) => {
  const paid = refData?.paid_referrals ?? 0;
  const milestones = refData?.milestones ?? [];
  const nextTier = refData?.next_milestone;

  // progress toward next tier; if all reached, show 100%
  let progressPct = 100;
  let progressLabel = 'ALL_TIERS_UNLOCKED';
  if (nextTier) {
    // previous threshold (last reached tier) or 0
    const prev = [...milestones].reverse().find((m) => m.reached);
    const from = prev ? prev.threshold : 0;
    const to = nextTier.threshold;
    const span = Math.max(1, to - from);
    const done = Math.max(0, paid - from);
    progressPct = Math.min(100, Math.round((done / span) * 100));
    progressLabel = `${paid} / ${to} PAID_REFERRALS`;
  }

  return (
    <div data-testid={MANAGE.milestoneCard} className="c-card col-span-1 lg:col-span-3">
      <div className="border-b border-[#3F3F46] pb-3 mb-5 flex items-center justify-between">
        <div className="text-[10px] tracking-[0.3em] uppercase text-gray-500">
          // REFERRAL_MILESTONES · REFER-3, GET-3-FREE
        </div>
        <span
          data-testid={MANAGE.milestoneTier}
          className={
            currentTier
              ? `c-badge c-badge--active`
              : 'c-badge'
          }
          style={currentTier ? { color: TIER_STYLES[currentTier]?.color, borderColor: `${TIER_STYLES[currentTier]?.color}55` } : undefined}
        >
          {currentTier ? `${TIER_STYLES[currentTier].label}_TIER` : 'NO_TIER_YET'}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-6">
        {milestones.map((m) => {
          const Icon = m.reached ? Award : Lock;
          const style = TIER_STYLES[m.key];
          return (
            <div
              key={m.key}
              className={`border p-4 relative overflow-hidden transition-colors ${
                m.reached ? 'border-[#38BDF8]/60 bg-[#38BDF8]/[0.03]' : 'border-[#3F3F46] bg-black'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon
                    className="w-4 h-4"
                    strokeWidth={2.5}
                    style={{ color: m.reached ? style.color : '#52525b' }}
                  />
                  <span
                    className="text-xs tracking-[0.2em] uppercase font-bold"
                    style={{ color: m.reached ? style.color : '#a1a1aa' }}
                  >
                    {style.label}
                  </span>
                </div>
                <span className="text-[10px] tracking-widest uppercase text-gray-600">
                  {m.threshold} REF
                </span>
              </div>
              <div className={`text-2xl font-bold ${m.reached ? 'text-[#38BDF8]' : 'text-gray-600'}`}>
                +{m.bonus_months} MO
              </div>
              <div className="mt-2 text-[10px] tracking-widest uppercase text-gray-600">
                {m.reached ? 'UNLOCKED · CREDITED' : 'LOCKED'}
              </div>
            </div>
          );
        })}
      </div>

      <div className="border border-[#3F3F46] bg-black p-4">
        <div className="flex items-center justify-between text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-2">
          <span>PROGRESS</span>
          <span data-testid={MANAGE.milestoneNext}>
            {nextTier
              ? `NEXT: ${nextTier.name} (+${nextTier.bonus_months} MO) · ${nextTier.remaining} to go`
              : progressLabel}
          </span>
        </div>
        <div
          data-testid={MANAGE.milestoneProgress}
          className="w-full h-3 bg-[#27272A] border border-[#3F3F46] relative"
          role="progressbar"
          aria-valuenow={progressPct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full bg-[#38BDF8] transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="mt-2 text-[10px] tracking-widest uppercase text-gray-500">
          {progressLabel} · {progressPct}%
        </div>
      </div>
    </div>
  );
};

export default MilestoneCard;
